import { CheckCircle, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Grid2,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import { DatePicker } from "@mui/x-date-pickers";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import UploadCloud from "src/components/shared/icon/UploadCloud";
import { BatchStatus, COMMOM_STATUS, orderStatusFormatted, UNIT_LIST, userRoleType, WAREHOUSE_TRANSFER_CREATE_TYPE } from "src/constant";
import useAppGlobal from "src/context/useAppGlobal";
import useAuthGlobal from "src/context/useAuthGlobal";
import useImportWarehouseTransfer from "src/hooks/inventory/useImportWarehouseTransfer";
import useProviderList from "src/hooks/setting/provider/useProviderList";
import DeliveredReceipt from "src/pages/orders/all-order/export-form/DeliveredReceipt";
import useWarehouseTransferReview from "src/hooks/warehouse-transfer/useWarehouseTransferReview";
import useWarehouseTransferExport from "src/hooks/warehouse-transfer/useWarehouseTranferExport";
import useProcessLogDetail from "src/hooks/process-log/useProcessLogDetail";
import appConfig from "src/config/env";
import useDebounce from "src/hooks/useDebounce";
import dayjs from "dayjs";
import useSettingBatchCode from "src/hooks/setting/batch-code/useSettingBatchCode";
import useWTRemove from "src/hooks/warehouse-transfer/useWTRemove";
import usePaymentMethodList from "src/hooks/payment-method/usePaymentMethodList";
import useWTImportCode from "src/hooks/warehouse-transfer/useWTImportCode";
import useWTScanCreate from "src/hooks/warehouse-transfer/useWTScanCreate";
import useWarehouseTransferDetail from "src/hooks/warehouse-transfer/useWarehouseTransferDetail";
import useProductList from "src/hooks/product/useProductList";
import useWarehouseTransferStatusIn from "src/hooks/warehouse-transfer/useWarehouseTransferStatusIn";
import useWTUpdateCode from "src/hooks/warehouse-transfer/useWTUpdateCode";
import useProductDetail from "src/hooks/product/useProductDetail";
import { unitConvert } from "src/helper/unitConvert";
import usePModelList from "src/hooks/product/usePModelList";
import useWTUpdate from "src/hooks/warehouse-transfer/useWTUpdate";
import ImportProductList from "./ImportProductList";
import ImportReviewDialog from "./ImportReviewDialog";
import MallScannerMUI from "src/components/shared/ScanningInput";
import { formatNumberWithDot } from "src/helper/convertNum";
import { NumericFormat } from "react-number-format";
import convertCurrency from "src/helper/convertCurrency";
import useWareHouseList from "src/hooks/warehouse/useWareHouseList";
import SearchList from "src/components/shared/SearchList";

const requiredFilter = {
  limit: 100,
  skip: 0,
  sortField: "createdAt",
  sortOrder: "desc",
};

export default function ImportFormDialog({ item, title, mode, mutateList }) {
  const inputRef = useRef(null);
  const scanRef = useRef(null);
  const { userMe } = useAuthGlobal();
  const isSuperAdmin = userMe?.role?.name === userRoleType.superAdmin;
  const { closeDialog, handleOpenDialog } = useAppGlobal();
  const [prodList, setProdList] = useState([]);
  const [prodPick, setProdPick] = useState({
    files: [],
    discount: 0,
  });
  const [stableField, setStableField] = useState({ toWarehouse: userMe?.currentWarehouse });
  const [isPrinted, setIsPrinted] = useState();
  const [logId, setLogId] = useState();
  const [wTDetailId, setWTDetail] = useState();
  const [activeScan, setActiveScan] = useState(true);
  const [partialScan, setPartialScan] = useState(false);
  const debounceItemCodes = useDebounce(prodPick?.itemCodes, 200);
  const isFileUploaded = Array.isArray(prodPick?.files) && prodPick.files.length > 0;
  const { data: product, isLoading } = useProductList({
    limit: 200,
    skip: 0,
  });
  const { data: provider } = useProviderList({ ...requiredFilter });
  const { trigger: createImport } = useImportWarehouseTransfer();
  const { trigger: removeWTSlip } = useWTRemove(wTDetailId || item?._id);
  const { trigger: createProductCode } = useWTImportCode(wTDetailId || item?._id);
  const { trigger: scanCode } = useWTScanCreate(wTDetailId || item?._id);
  const { data: scanDetail } = useWarehouseTransferDetail(wTDetailId || item?._id);
  const { data } = useWarehouseTransferReview(isPrinted, {
    transferFileType: orderStatusFormatted.Imported,
  });
  const { data: paymentMethod } = usePaymentMethodList();
  const { trigger: printWT } = useWarehouseTransferExport();
  const { mutate } = useProcessLogDetail(logId);
  const { data: batchCode } = useSettingBatchCode();
  const { trigger: importWT } = useWarehouseTransferStatusIn(wTDetailId || item?._id);
  const { trigger: updateProd } = useWTUpdateCode(wTDetailId || item?._id);
  const { data: detail, isLoading: detailLoading } = useWarehouseTransferDetail(item?._id ?? wTDetailId);
  const { data: prodDetail, isLoading: prodDetailLoading } = useProductDetail(prodPick?.productId);
  const { data: pModelList } = usePModelList(prodPick?.productId?._id, {
    limit: 200,
    skip: 0,
  });
  const { trigger: updateWT } = useWTUpdate(detail?.data?._id);

  const isImportSlipReadOnly = useMemo(() => {
    if (mode !== "update") return false;
    const transfer = detail?.data ?? item;
    const slipProviderId = transfer?.provider?._id ?? transfer?.provider;
    const slipFromWarehouseId = transfer?.fromWarehouse?._id ?? transfer?.fromWarehouse;
    const isNccSlip = Boolean(slipProviderId);
    const isInternalWarehouseSlip = Boolean(slipFromWarehouseId) && !isNccSlip;
    return isInternalWarehouseSlip || (isNccSlip && transfer?.statusIn !== BatchStatus.Pending);
  }, [mode, item, detail?.data]);

  const handleChangeValue = ({ name, value }) => {
    setProdPick((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (prodDetail?.data?.unit) {
      setProdPick((prev) => ({ ...prev, unit: prodDetail?.data?.unit }));
    }
  }, [prodDetail?.data?.unit, prodDetailLoading, prodDetail?.productId, prodPick?.unit]);

  const handleChangeStableField = ({ name, value }) => {
    const itemStableFields = { ...stableField, [name]: value };
    if (name === "toWarehouse") {
      itemStableFields.provider = undefined;
      setProdPick((prev) => ({
        ...prev,
        productId: undefined,
        productModelId: undefined,
      }));
    }
    setStableField(itemStableFields);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = null;

    try {
      const data = await readExcelFile(file);

      if (!data[0] || !("itemCodes" in data[0])) {
        enqueueSnackbar("File excel phải có itemCodes", { variant: "warning" });
      }

      const validRows = data.filter((row) => row.itemCodes && row.itemCodes.trim() !== "");

      setProdPick((prev) => {
        const qty = prev?.qty ?? 0;

        if (validRows.length !== qty) {
          enqueueSnackbar("Mã sản phẩm và số lượng không khớp", {
            variant: "warning",
          });
        }

        return {
          ...prev,
          files: [...(Array.isArray(prev?.files) ? prev.files : []), file],
        };
      });
    } catch (error) {
      console.error("❌ Error processing file:", error);
    }
  };

  // ADD PROD
  const handleAddMoreProdModel = (transferDetailId) => {
    const isUpload = prodPick?.files?.length > 0;
    const matchedProduct = product?.data?.find((v) => v?._id === prodPick?.productId);

    const productModels = pModelList?.data?.find((v) => v?.product === prodPick?.productId);

    const index = prodList?.findIndex((v) => v?.batchCode === prodPick?.batchCode);
    let newItem = {
      ...prodPick,
      batchName: prodPick?.batchCode,
      productName: matchedProduct?.name,
      productMolName: productModels?.name,
      tempPrice: productModels?.price,
      qty: prodPick?.qtyCal,
      totalPrice: productModels?.price * Number(prodPick?.qtyCal) * (1 - Number(prodPick?.discount || 0) / 100),
      transferDetailId: transferDetailId,
    };

    if (isUpload) {
      const fileIndex = prodList.filter((item) => item?.files?.length > 0).length;
      newItem.fileIndex = fileIndex;
    }

    if (index === -1) {
      setProdList((prev) => [...prev, newItem]);
    } else {
      const itemProd = [...prodList];
      itemProd[index] = { ...itemProd[index], ...newItem };
      setProdList(itemProd);
    }

    enqueueSnackbar("Thêm sản phẩm thành công", { variant: "success" });
  };

  // CREATE IMPORT SLIP
  const handleCreateImport = async () => {
    try {
      const res = await createImport({
        body: {
          toWarehouse: stableField.toWarehouse?._id ?? userMe?.currentWarehouse?._id,
          statusIn: orderStatusFormatted.Draft,
          paymentMethod: stableField.paymentMethod,
          provider: stableField.provider,
          note: stableField?.note,
          invoice_code: stableField?.invoice_code,
        },
      });
      if (res?.success) {
        return setWTDetail(res?.data?._id);
      } else {
        return enqueueSnackbar(res?.error?.message, { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Đã có lỗi xảy ra", { variant: "error" });
    }
  };

  //AUTO CREATING IMPORT
  useEffect(() => {
    if (!!stableField?.paymentMethod && mode !== "update") {
      handleCreateImport();
    }
  }, [stableField?.paymentMethod, mode]);

  const handlePrint = async () => {
    const res = await printWT({
      body: {
        warehouseTransferIds: [isPrinted],
        transferFileType: orderStatusFormatted.Imported,
      },
    });
    if (res?.success) {
      setLogId(res?.data?._id);
      handleGetLog(res?.data?._id);
      return enqueueSnackbar("Tiến hành xuất phiếu....", { variant: "success", className: "notistack-confirm" });
    } else {
      return enqueueSnackbar(res?.error?.message ?? res?.error?.message, {
        variant: "error",
      });
    }
  };

  const handleGetLog = (logIdParam) => {
    const logIdToUse = logIdParam || logId;
    if (!logIdToUse) return;

    const timeOut = setInterval(async () => {
      try {
        const res = await mutate();
        if (res?.success) {
          if (res.data.status === COMMOM_STATUS.done) {
            closeDialog();
            if (Array.isArray(res.data?.urls)) {
              res.data.urls.forEach((v) => {
                window.open(`${appConfig.API_FILE}/${v}`, "_blank");
              });
            }
            clearInterval(timeOut);
          }

          if (res.data.status === COMMOM_STATUS.error) {
            enqueueSnackbar("Không xuất được đơn", { variant: "error" });
            clearInterval(timeOut);
          }
        }
      } catch (err) {
        clearInterval(timeOut);
      }
    }, 1000);
  };

  const createProd = async (status) => {
    if (mode === "update" && isImportSlipReadOnly) {
      return enqueueSnackbar("Không thể chỉnh sửa phiếu nhập kho này.", { variant: "warning" });
    }
    const formData = new FormData();
    formData.append("toWarehouse", stableField?.toWarehouse ?? userMe?.currentWarehouse?._id ?? "");
    formData.append("productModel", prodPick.productModelId);
    formData.append("batchCode", prodPick.batchCode);
    formData.append("mfg", prodPick.mfg);
    formData.append("exp", prodPick.exp);
    formData.append(
      "qty",
      !["piece", "bottle", "combo"]?.includes(prodPick?.unit)
        ? unitConvert({
            val: Number(prodPick.qtyCal),
            from: prodPick?.unit,
            to: prodDetail?.data?.unit,
          })
        : Number(prodPick.qtyCal),
    );
    formData.append("discount", prodPick.discount);
    formData.append("lotNSX", prodPick.lotNSX);
    formData.append("discount_price", prodPick.discount_price ?? 0);
    formData.append("vat", prodPick.vat ?? 0);

    if (Array.isArray(prodPick?.files)) {
      prodPick.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    const res = await createProductCode({
      body: formData,
    });
    if (!res?.success) {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    } else {
      handleAddMoreProdModel(res?.data?._id);
      setProdPick((prev) => ({
        ...prev,
        transferDetailId: res?.data?._id,
        qtyCal: res?.data?.qty,
      }));
      if (status === WAREHOUSE_TRANSFER_CREATE_TYPE.CREATE) {
        setProdPick();
      }
    }
  };

  const handleUpdateProd = async () => {
    if (mode === "update" && isImportSlipReadOnly) {
      return enqueueSnackbar("Không thể chỉnh sửa phiếu nhập kho này.", { variant: "warning" });
    }
    const formData = new FormData();
    formData.append("toWarehouse", stableField?.toWarehouse?._id ?? userMe?.currentWarehouse?._id ?? "");
    formData.append("discount", String(Number(prodPick?.discount)));
    formData.append("productModel", prodPick?.productModelId?._id ?? "");
    formData.append("batchCode", prodPick?.batchCode ?? "");
    formData.append("mfg", prodPick?.mfg ?? "");
    formData.append("exp", prodPick?.exp ?? "");
    formData.append("lotNSX", prodPick.lotNSX ?? "");
    formData.append(
      "qty",
      ["piece", "bottle", "combo"]?.includes(prodPick?.unit)
        ? String(Number(prodPick?.qtyCal))
        : unitConvert({
            val: Number(prodPick.qtyCal),
            from: prodPick?.unit,
            to: prodDetail?.data?.unit,
          }),
    );

    if (prodPick?.files && prodPick?.files?.length > 0) {
      Array.from(prodPick?.files).forEach((file, index) => {
        formData.append("files", file);
      });
    } else {
      formData.append("files", []);
    }
    const res = await updateProd({
      pathIncluded: {
        transferDetailId: prodPick?.transferDetailId,
      },
      body: formData,
    });
    if (res?.success) {
      await mutateList?.();
      setProdPick();
      await handleUpdateWT();
      const list = [...prodList];
      const index = list?.findIndex((v) => v?.transferDetailId === res?.data?._id);
      list[index] = { ...list[index], qtyCal: res?.data?.qty };
      setProdList(list);
      return enqueueSnackbar("Cập nhật sản phẩm thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  useEffect(() => {
    if (!!debounceItemCodes) {
      const refinedArray = debounceItemCodes?.map((v) => v?.replaceAll("Shift", ""));
      if (prodDetail?.data?.productGroup?.skipValidItemCode) {
        handleCheckBatch(refinedArray, prodPick?.transferDetailId);
      } else {
        const index = prodList?.findIndex((v) => v?.batchName === prodPick?.batchCode);
        if (index > -1) {
          const currentTransferDetailId = prodList[index]?.transferDetailId;
          setProdPick((prev) => ({
            ...prev,
            transferDetailId: currentTransferDetailId,
          }));
          if (!Array.isArray(batchCode?.data?.stripItemCodes)) return;

          const validCodes = refinedArray.map((line) => {
            let cleaned = line;
            batchCode.data.stripItemCodes.forEach((strip) => {
              cleaned = cleaned.replace(strip, "");
            });
            return cleaned.trim();
          });

          handleCheckBatch(validCodes, currentTransferDetailId);
        }
      }
    }
  }, [debounceItemCodes, prodPick?.batchCode, batchCode?.data?.stripItemCodes]);

  const handleFilterErrorCode = (key) => {
    setProdPick((prev) => ({ ...prev, itemCodes: null }));
    closeSnackbar(key);
  };

  const handleConfirm = (errorObj) => {
    const action = (key) => (
      <Button
        onClick={() => handleFilterErrorCode(key)}
        sx={{ bgcolor: "var(--black)" }}
        variant="contained"
        size="small"
      >
        Xác nhận
      </Button>
    );
    return enqueueSnackbar(
      <div>
        <p style={{ margin: 0 }}>Vui lòng xác nhận xoá các mã lỗi:</p>
        {!!errorObj?.createFailed?.length && (
          <p style={{ margin: 0 }}>
            Mã lỗi: <strong>{String(errorObj?.createFailed)}</strong>
          </p>
        )}
        {!!errorObj?.duplicate?.length && (
          <p style={{ margin: 0 }}>
            Mã trùng: <strong>{String(errorObj?.duplicate)}</strong>
          </p>
        )}
        {!!errorObj?.existed?.length && (
          <p style={{ margin: 0 }}>
            Mã đã tồn tại: <strong>{String(errorObj?.existed)}</strong>
          </p>
        )}
        {!!errorObj?.invalidPrefix?.length && (
          <p style={{ margin: 0 }}>
            Sai format: <strong>{String(errorObj?.invalidPrefix)}</strong>
          </p>
        )}
        {errorObj === "BatItemCodeExceed" && <p style={{ margin: 0, fontWeight: "bold" }}>Mã sản phẩm vượt quá số lượng cần nhập</p>}
      </div>,
      {
        variant: "warning",
        action,
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    );
  };

  const handleCheckBatch = async (item, transferDetailIdParam) => {
    if (mode === "update" && isImportSlipReadOnly) return;
    const transferDetailId = transferDetailIdParam ?? prodPick?.transferDetailId;
    if (!transferDetailId) return;
    const res = await scanCode({
      pathIncluded: {
        transferDetailId,
      },
      body: {
        itemCodes: item,
      },
    });

    if (res?.success) {
      if (res?.data?.itemCodes?.totalError > 0) {
        handleConfirm(res?.data?.itemCodes?.errorItemCodes);
      } else {
        setProdPick((prev) => ({ ...prev, itemCodes: undefined }));
      }
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleRemoveImportedSlip = async () => {
    const res = await removeWTSlip();
    if (res.success) {
      closeDialog();
      return enqueueSnackbar("Đóng phiếu thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "success" });
    }
  };

  const handleClose = () => {
    if (stableField?.paymentMethod && !isPrinted && !item) {
      enqueueSnackbar({
        variant: "warning",
        persist: true,
        content: (key) => (
          <Paper
            elevation={6}
            sx={{
              p: 2,
              backgroundColor: "var(--pending-txt)",
              color: "var(--black)",
              maxWidth: 360,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography fontWeight="bold">Bạn có muốn xoá thông tin nhập kho hiện tại không?</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  closeSnackbar(key);
                }}
              >
                Hủy
              </Button>
              <Button
                size="small"
                sx={{ background: "var(--black)" }}
                variant="contained"
                onClick={() => {
                  closeSnackbar(key);
                  handleRemoveImportedSlip();
                }}
              >
                Đồng ý
              </Button>
            </Box>
          </Paper>
        ),
      });
    } else {
      closeDialog();
    }
  };

  const handleImportWTSlip = async (statusIn) => {
    if (mode === "update" && isImportSlipReadOnly) {
      return enqueueSnackbar("Không thể chỉnh sửa phiếu nhập kho này.", { variant: "warning" });
    }
    const res = await importWT({
      body: {
        statusIn: statusIn,
      },
    });
    if (res?.success) {
      setIsPrinted(res?.data?._id);
      return enqueueSnackbar("Tạo phiếu nhập thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const totalTransferDetail = useMemo(() => {
    const item = scanDetail?.data?.transferDetails?.find((v) => v?._id === prodPick?.transferDetailId);
    return item;
  }, [scanDetail?.data?.transferDetails, prodPick?.transferDetailId]);

  const handleUpdateWT = async () => {
    const res = await updateWT({
      body: {
        ...stableField,
      },
    });
    if (!res?.success) {
      return enqueueSnackbar("Cập nhập thông tin đơn hàng không thành công!", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (!detailLoading && detail?.data?.transferDetails?.length > 0) {
      setProdList(
        detail.data.transferDetails.map((v) => ({
          ...v,
          productName: v?.productModel?.product?.name,
          productMolName: v?.productModel?.name,
          batchName: v?.batch?.code,
          lotNSX: v?.batch?.lotNSX,
          qtyCal: v?.qty,
          unit: v?.productModel?.product?.unit,
          discount: v?.discount ?? 0,
          tempPrice: v?.price,
          productModelId: v?.productModel?._id,
          transferDetailId: v?._id,
          exp: v?.batch?.exp,
          mfg: v?.batch?.mfg,
        })),
      );
    }
  }, [detailLoading, detail?.data?.transferDetails]);

  useEffect(() => {
    if (item) {
      setStableField({
        toWarehouse: item?.toWarehouse?._id ?? userMe?.currentWarehouse?._id,
        provider: item?.provider?._id,
        paymentMethod: item?.paymentMethod?._id,
        note: item?.note,
      });
    }
  }, [item, userMe?.currentWarehouse?._id]);

  useEffect(() => {
    if (!item && userMe?.currentWarehouse?._id && !stableField?.toWarehouse) {
      setStableField((prev) => ({ ...prev, toWarehouse: userMe?.currentWarehouse?._id }));
    }
  }, [item, userMe?.currentWarehouse?._id, stableField?.toWarehouse]);

  const handleUpdateFields = (item) => {
    setProdPick((prev) => ({
      ...prev,
      ...item,
      discount: item?.discount ?? 0,
      productId: item?.productModel?.product,
      productModelId: item?.productModel,
      batchCode: item?.batch?.code,
    }));
  };

  const isScanned = Array.isArray(detail?.data?.transferDetails) ? detail?.data?.transferDetails?.reduce((acc, curr) => acc + (curr?.importedQty || 0), 0) : 0;

  const handleReview = (val, type) => {
    handleOpenDialog({
      title: `Xem  ${val?.statusIn === orderStatusFormatted.Pending ? "thông tin" : "phiếu"} nhập kho`,
      content: (
        <ImportReviewDialog
          val={val}
          transferFileType={type}
        />
      ),
      size: "xl",
    });
  };

  const hasPartialScannedProduct = Array.isArray(detail?.data?.transferDetails)
    ? detail.data.transferDetails.some((transferDetail) => {
        const importedQty = Number(transferDetail?.importedQty ?? 0);
        const totalQty = Number(transferDetail?.qty ?? 0);
        return importedQty > 0 && importedQty < totalQty;
      })
    : false;

  useEffect(() => {
    if (!activeScan) {
      scanRef.current.focus();
    }
  }, [activeScan]);

  useEffect(() => {
    if (prodList?.length) {
      const item = prodList?.find((v) => v?.batch === prodPick?.batch);
      if ((item?.importedQty ?? 0) > 0) {
        setPartialScan(true);
      } else {
        setPartialScan(false);
      }
    }
  }, [prodList?.length, prodPick?.batch]);

  console.log(prodPick?.productModelId);

  return (
    <>
      <DialogTitle
        fontWeight="bold"
        display="flex"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid var(--grey)", mb: "8px" }}
      >
        {!isPrinted && !title && "Tạo lệnh nhập kho"}
        {title && title}

        <IconButton
          onClick={handleClose}
          sx={{ width: "24px", height: "24px", ml: "auto" }}
        >
          <Close sx={{ fontSize: "22px" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <>
          {!isPrinted ? (
            <Grid2
              container
              spacing={2}
              mt={1}
            >
              <Grid2
                size={{
                  xs: 5,
                  overflow: "auto !important",
                  height: "100vh !important",
                  pr: "24px",
                }}
                item
              >
                <Stack spacing={1.75}>
                  <Box>
                    <SearchList
                      params={{ path: "/api/v1/warehouses", params: { limit: 20, skip: 0 } }}
                      label="Kho nhận"
                      value={stableField?.toWarehouse || ""}
                      onChange={(_, e) => handleChangeStableField({ name: "toWarehouse", value: e })}
                    />
                    {/* <FormControl fullWidth>
                      <InputLabel id="warehouse-select-label">Kho</InputLabel>
                      <Select
                        disabled={!isSuperAdmin || isScanned > 0}
                        labelId="warehouse-select-label"
                        id="warehouse-select"
                        label="Kho"
                        value={stableField?.toWarehouse ?? userMe?.currentWarehouse?._id ?? ""}
                        onChange={(e) =>
                          handleChangeStableField({
                            name: "toWarehouse",
                            value: e.target.value,
                          })
                        }
                      >
                        {!isSuperAdmin ? (
                          Array.isArray(warehouseList?.data) ? (
                            warehouseList?.data?.map((v) => (
                              <MenuItem
                                key={v?._id}
                                value={v?._id}
                              >
                                {v?.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem>Không có dữ liệu</MenuItem>
                          )
                        ) : Array.isArray(warehouseList?.data) ? (
                          warehouseList?.data
                            ?.filter((val) => val?._id !== userMe?.currentWarehouse?._id)
                            ?.map((v) => (
                              <MenuItem
                                key={v?._id}
                                value={v?._id}
                              >
                                {v?.name}
                              </MenuItem>
                            ))
                        ) : (
                          <MenuItem>Không có dữ liệu</MenuItem>
                        )}
                      </Select>
                    </FormControl> */}
                  </Box>
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">NCC</InputLabel>
                      <Select
                        disabled={isScanned > 0 || isImportSlipReadOnly}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="NCC"
                        value={stableField?.provider ?? ""}
                        onChange={(e) =>
                          handleChangeStableField({
                            name: "provider",
                            value: e.target.value,
                          })
                        }
                      >
                        {Array.isArray(provider?.data) ? (
                          provider?.data?.map((v) => {
                            return (
                              <MenuItem
                                key={v?._id}
                                value={v?._id}
                              >
                                {v?.name}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem>Không có dữ liệu</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">Hình thức thanh toán</InputLabel>
                      <Select
                        disabled={!stableField?.provider || isScanned > 0 || isImportSlipReadOnly}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Hình thức thanh toán"
                        value={stableField?.paymentMethod ?? ""}
                        onChange={(e) =>
                          handleChangeStableField({
                            name: "paymentMethod",
                            value: e.target.value,
                          })
                        }
                      >
                        {Array.isArray(paymentMethod?.data) && paymentMethod?.data?.length > 0 ? (
                          paymentMethod?.data?.map((v) => {
                            return (
                              <MenuItem
                                key={v?._id}
                                value={v?._id}
                              >
                                {v?.name}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem>Không có dữ liệu</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    disabled={isScanned > 0 || isImportSlipReadOnly}
                    rows={1}
                    value={stableField?.note || ""}
                    label="Ghi chú"
                    onChange={(e) => setStableField((prev) => ({ ...prev, note: e.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Số hoá đơn (Nếu có)"
                    // disabled={!prodPick?.qtyCal}
                    // value={stableField?.invoice_code ?? ""}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                    onChange={(e) => setStableField((prev) => ({ ...prev, name: "invoice_code", value: e.target.value }))}
                  />
                  <Typography fontWeight="bold">Thông tin sản phẩm</Typography>
                  <SearchList
                    label="Sản phẩm"
                    disabled={partialScan || isImportSlipReadOnly}
                    value={prodPick?.productId || ""}
                    params={{ path: "/api/v1/products", params: { limit: 20, skip: 0 } }}
                    onChange={(_, e) => handleChangeValue({ name: "productId", value: e })}
                  />
                  {/* <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label"> Sản phẩm</InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      disabled={partialScan || isImportSlipReadOnly}
                      value={prodPick?.productId || ""}
                      label="Sản phẩm"
                      onChange={(e) => handleChangeValue({ name: "productId", value: e.target.value })}
                    >
                      {Array.isArray(product?.data) ? (
                        product?.data?.map((v) => {
                          return (
                            <MenuItem
                              key={v?._id}
                              value={v?._id}
                            >
                              {v?.name}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem>Không có dữ liệu</MenuItem>
                      )}
                    </Select>
                  </FormControl> */}
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label"> Phân loại sản phẩm</InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={prodPick?.productModelId?._id || ""}
                      label="Phân loại sản phẩm"
                      disabled={!prodPick?.productId || partialScan || isImportSlipReadOnly}
                      onChange={(e) =>
                        handleChangeValue({
                          name: "productModelId",
                          value: e.target.value,
                        })
                      }
                    >
                      {Array.isArray(pModelList?.data) ? (
                        pModelList?.data?.map((val) => (
                          <MenuItem
                            key={val?._id}
                            value={val?._id}
                          >
                            {val?.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem>Không có dữ liệu</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <TextField
                    disabled={!prodPick?.productModelId || partialScan || isImportSlipReadOnly}
                    fullWidth
                    label="Số lô NSX"
                    value={prodPick?.lotNSX || ""}
                    slotProps={{ inputProps: { min: 0 } }}
                    onChange={(e) => handleChangeValue({ name: "lotNSX", value: e.target.value })}
                  />
                  <TextField
                    disabled={!prodPick?.lotNSX || partialScan || isImportSlipReadOnly}
                    fullWidth
                    label="Số lô nhập"
                    value={prodPick?.batchCode || ""}
                    slotProps={{ inputProps: { min: 0 } }}
                    onChange={(e) => handleChangeValue({ name: "batchCode", value: e.target.value })}
                  />
                  <Box sx={{ display: "flex", gap: "12px" }}>
                    <DatePicker
                      value={prodPick?.mfg ? dayjs(prodPick?.mfg) : null}
                      sx={{ width: "100%" }}
                      label="Ngày sản xuất"
                      format="DD/MM/YYYY"
                      disabled={!prodPick?.batchCode || partialScan || isImportSlipReadOnly}
                      onChange={(newValue) => {
                        if (newValue && dayjs(newValue).isValid()) {
                          handleChangeValue({
                            name: "mfg",
                            value: newValue.toISOString(),
                          });
                        } else {
                          handleChangeValue({
                            name: "mfg",
                            value: null,
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          onKeyDown: (e) => {
                            if (e.key === "Enter") e.preventDefault();
                          },
                        },
                      }}
                    />

                    <DatePicker
                      sx={{ width: "100%" }}
                      label={"Ngày hết hạn"}
                      fullWidth
                      format="DD/MM/YYYY"
                      value={prodPick?.exp ? dayjs(prodPick?.exp) : null}
                      disabled={!prodPick?.mfg || partialScan || isImportSlipReadOnly}
                      onChange={(newValue) => {
                        if (newValue && dayjs(newValue).isValid()) {
                          handleChangeValue({
                            name: "exp",
                            value: newValue.toISOString(),
                          });
                        } else {
                          handleChangeValue({
                            name: "mfg",
                            value: null,
                          });
                        }
                      }}
                      slotProps={{
                        textField: {
                          onKeyDown: (e) => {
                            if (e.key === "Enter") e.preventDefault();
                          },
                        },
                      }}
                      shouldDisableDate={(date) => {
                        return dayjs(date).isBefore(dayjs(prodPick?.mfg));
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Box>
                  <Box sx={{ mt: "16px" }}>
                    <TextField
                      fullWidth
                      type="number"
                      disabled={!prodPick?.exp || isImportSlipReadOnly}
                      value={prodPick?.qtyCal ?? ""}
                      label="Số lượng"
                      error={prodPick?.qtyCal < totalTransferDetail?.importedQty}
                      helperText={prodPick?.qtyCal < totalTransferDetail?.importedQty ? "Số lượng không được nhỏ hơn sl đã scan" : null}
                      onChange={(e) => setProdPick((prev) => ({ ...prev, qtyCal: Number(e.target.value) }))}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{ pl: 1, pr: 0.5, pointerEvents: "auto" }}
                          >
                            <Select
                              key={prodPick?.unit}
                              size="small"
                              value={prodPick?.unit}
                              label="Đơn vị nhập"
                              disabled={!prodPick?.unit || isImportSlipReadOnly}
                              onChange={(e) => setProdPick((prev) => ({ ...prev, unit: e.target.value }))}
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                "& .MuiSelect-select": { pr: "24px" },
                              }}
                            >
                              {UNIT_LIST.filter((v) => v?.type?.includes(prodPick?.unit))?.map((v) => {
                                return (
                                  <MenuItem
                                    key={v?.id}
                                    value={v?.id}
                                  >
                                    {v?.label}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        min: 0,
                      }}
                    />
                  </Box>
                  <TextField
                    type="number"
                    fullWidth
                    label="Chiết khấu (%)"
                    disabled={!prodPick?.qtyCal || isImportSlipReadOnly}
                    value={prodPick?.discount ?? 0}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                    onChange={(e) => {
                      const val = +e?.target?.value;
                      if (val > 100 || val < 0) {
                        enqueueSnackbar("Vượt quá chiết khấu cho phép", { variant: "error" });
                        return;
                      } else {
                        handleChangeValue({ name: "discount", value: e.target.value });
                      }
                    }}
                  />
                  <NumericFormat
                    fullWidth
                    disabled={isImportSlipReadOnly}
                    label="Số tiền giảm giá"
                    thousandSeparator
                    customInput={TextField}
                    InputProps={{
                      inputProps: { min: 0 },
                      startAdornment: <InputAdornment position="start">đ{prodPick?.discount_price ? "" : 0}</InputAdornment>,
                    }}
                    onValueChange={(values) => handleChangeValue({ name: "discount_price", value: values?.floatValue })}
                  />
                  <Typography
                    fontWeight="bold"
                    fontSize="12px"
                  >
                    ({prodPick?.discount_price ? convertCurrency(prodPick?.discount_price ?? 0) : 0} đồng)
                  </Typography>
                  <TextField
                    type="number"
                    fullWidth
                    label="VAT (%)"
                    disabled={isImportSlipReadOnly}
                    value={prodPick?.vat ?? 0}
                    InputProps={{ inputProps: { min: 0 } }}
                    onChange={(e) => {
                      const val = +e?.target?.value;
                      if (val < 0) {
                        enqueueSnackbar("Không nhập VAT bé hơn 0", { variant: "error" });
                        return;
                      } else {
                        handleChangeValue({ name: "vat", value: e.target.value });
                      }
                    }}
                  />
                  <MallScannerMUI
                    getValue={setProdPick}
                    value={prodPick}
                    disabled={isImportSlipReadOnly || totalTransferDetail?.importedQty === (Number(prodPick?.qtyCal) ?? 0) || !prodPick?.discount}
                    full={totalTransferDetail?.importedQty !== 0 && totalTransferDetail?.importedQty === (prodPick?.qtyCal ? formatNumberWithDot(Number(prodPick?.qtyCal)) : 0)}
                  />
                  <Typography
                    flex={1}
                    sx={{
                      mt: "0px !important",
                      fontWeight: "bold",
                      color: prodPick?.itemCodes?.length > Number(prodPick?.qtyCal) ? "var(--error)" : "var(--black)",
                    }}
                  >
                    Đã scan: {`${totalTransferDetail?.importedQty ?? 0}/${!!prodPick?.qtyCal ? formatNumberWithDot(Number(prodPick?.qtyCal)) : 0}`}
                  </Typography>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    disabled={!prodPick?.qtyCal || isImportSlipReadOnly}
                    fullWidth
                    variant="outlined"
                    startIcon={!isFileUploaded ? <UploadCloud /> : <CheckCircle color="success" />}
                    sx={{ height: "100%", display: "flex", justifyContent: "left" }}
                    onClick={handleClick}
                  >
                    {!isFileUploaded ? "Tải lên danh sách số serial" : "Đã tải lên 1 file"}
                  </Button>
                </Stack>
              </Grid2>
              {/* TABLE */}
              <Grid2
                size={{ xs: 7 }}
                item
              >
                <ImportProductList
                  list={prodList}
                  setProdPick={setProdPick}
                  setProdList={setProdList}
                  onUpdate={handleUpdateFields}
                  mutateList={mutateList}
                  readOnly={isImportSlipReadOnly}
                />
              </Grid2>
            </Grid2>
          ) : (
            <DeliveredReceipt
              isLoading={isLoading}
              html={data}
            />
          )}
        </>
      </DialogContent>
      <DialogActions sx={{ p: "12px 24px 24px" }}>
        {!isPrinted ? (
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={5}
              align="right"
            >
              <Button
                fullWidth
                sx={{ background: "var(--confirm)", color: "var(--confirm-txt)" }}
                variant="contained"
                onClick={() => {
                  if ((!prodPick?.transferDetailId && !prodList?.length) || (!prodPick?.transferDetailId && prodPick?.qtyCal)) {
                    createProd(WAREHOUSE_TRANSFER_CREATE_TYPE.CREATE);
                    setProdPick();
                  }
                  if (prodPick?.transferDetailId && prodList?.length) {
                    setProdPick();
                  }
                  if (prodPick?.mode === "update") {
                    handleUpdateProd();
                  }
                }}
                disabled={isImportSlipReadOnly || prodPick?.discount === null || !prodPick?.discount || prodPick?.qtyCal < totalTransferDetail?.importedQty}
              >
                {prodPick?.mode === "update" ? "Sửa sản phẩm" : "Thêm sản phẩm"}
              </Button>
            </Grid>
            <Grid
              item
              xs={7}
            >
              <Box
                display="flex"
                gap="12px"
              >
                {detail?.data?.statusIn !== "Pending" ? (
                  <Button
                    disabled={isImportSlipReadOnly || !prodList?.length || hasPartialScannedProduct}
                    sx={{ whiteSpace: "nowrap" }}
                    variant="outlined"
                    fullWidth
                    onClick={() => handleImportWTSlip(orderStatusFormatted.Pending)}
                  >
                    Chờ nhập kho
                  </Button>
                ) : (
                  <Button
                    disabled={isImportSlipReadOnly || hasPartialScannedProduct}
                    sx={{ whiteSpace: "nowrap" }}
                    variant="outlined"
                    fullWidth
                    onClick={() => handleReview(item, orderStatusFormatted.Imported)}
                  >
                    Xác nhận thay đổi
                  </Button>
                )}

                <Button
                  disabled={isImportSlipReadOnly || !prodList?.length || hasPartialScannedProduct}
                  form="export-form"
                  type="submit"
                  sx={{ whiteSpace: "nowrap", display: "flex", flexDirection: "column" }}
                  variant="contained"
                  fullWidth
                  onClick={() => handleImportWTSlip(orderStatusFormatted.Imported)}
                >
                  Tạo phiếu nhập
                </Button>
              </Box>
            </Grid>
            <Grid
              xs={12}
              item
              mt="-12px"
            >
              <Typography
                fontSize={10}
                textAlign="right"
                fontWeight="bold"
              >
                {(!!item?.provider || !!detail?.data?.provider) && "(Nếu đã scan mã sản phẩm thì cần scan đủ theo từng sản phẩm)"}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={handlePrint}
          >
            In phiếu
          </Button>
        )}
      </DialogActions>
    </>
  );
}
